import React, { useState, useEffect, useRef } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal, Form,
    Spinner, Alert, Table, InputGroup, ProgressBar
} from 'react-bootstrap';
import {
    Receipt, Plus, Download, CheckCircle, X,
    Upload, Eye, AlertCircle, ChevronRight,
    DollarSign, Clock, BarChart2, Settings,
    FileText, RefreshCw, Filter
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

// ── Constants ────────────────────────────────────────────────────────────────
const HR_ROLES = ['super-admin','admin','hr-manager','account-manager'];

const STATUS_CONFIG = {
    pending:       { bg:'warning',   label:'Pending'        },
    approved:      { bg:'success',   label:'Approved'       },
    auto_approved: { bg:'success',   label:'Auto-Approved'  },
    rejected:      { bg:'danger',    label:'Rejected'       },
    paid:          { bg:'primary',   label:'Paid'           },
    withdrawn:     { bg:'secondary', label:'Withdrawn'      },
};

const CURR_YEAR  = new Date().getFullYear();
const CURR_MONTH = new Date().getMonth() + 1;
const MONTHS     = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtCurrency = (n) =>
    `₹${parseFloat(n||0).toLocaleString('en-IN',{ minimumFractionDigits:2, maximumFractionDigits:2 })}`;

const fmtDate = (d) => {
    if (!d) return '—';
    const [y,m,dd] = String(d).slice(0,10).split('-').map(Number);
    return new Date(y,m-1,dd).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'});
};

// ════════════════════════════════════════════════════════════════════════════
const ExpensesPage = () => {
    const { user } = useAuth();
    const isHR     = HR_ROLES.includes(user?.role);
    const [tab, setTab] = useState('my');

    const tabs = [
        { key:'my',       label:'My Claims',     icon:Receipt,   show:true     },
        { key:'pending',  label:'For Approval',  icon:Clock,     show:true     },
        { key:'all',      label:'All Claims',    icon:FileText,  show:isHR     },
        { key:'stats',    label:'Analytics',     icon:BarChart2, show:isHR     },
        { key:'settings', label:'Policy & Setup',icon:Settings,  show:isHR     },
    ].filter(t => t.show);

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <Receipt size={22} className="me-2 text-primary"/>Expense Management
                    </h4>
                    <p className="text-muted small mb-0">
                        Submit receipts · Track reimbursements · Get paid faster
                    </p>
                </div>
                {/* <Button variant="primary" size="sm" onClick={() => setTab('my')}>
                    <Plus size={14} className="me-1"/>New Claim
                </Button> */}
            </div>

            <div className="d-flex gap-1 flex-wrap mb-4">
                {tabs.map(t => {
                    const Icon = t.icon;
                    return (
                        <Button key={t.key} size="sm"
                            variant={tab===t.key ? 'primary' : 'outline-secondary'}
                            onClick={() => setTab(t.key)}>
                            <Icon size={13} className="me-1"/>{t.label}
                        </Button>
                    );
                })}
            </div>

            {tab === 'my'       && <MyClaimsTab isHR={isHR}/>}
            {tab === 'pending'  && <PendingTab isHR={isHR}/>}
            {tab === 'all'      && isHR && <AllClaimsTab/>}
            {tab === 'stats'    && isHR && <StatsTab/>}
            {tab === 'settings' && isHR && <SettingsTab/>}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// MY CLAIMS TAB
// ════════════════════════════════════════════════════════════════════════════
const MyClaimsTab = ({ isHR }) => {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [viewing, setViewing] = useState(null);

    const load = () => {
        setLoading(true);
        axiosInstance.get('/expenses/my')
            .then(r => setData(r.data.data))
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const withdraw = async (id) => {
        if (!window.confirm('Withdraw this claim?')) return;
        try {
            await axiosInstance.delete(`/expenses/${id}`);
            toast.success('Claim withdrawn');
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;

    const claims   = data?.claims || [];
    const monthly  = data?.this_month;

    return (
        <>
            {/* Monthly summary bar */}
            {monthly && (monthly.pending_count > 0 || monthly.approved_count > 0) && (
                <Row className="g-3 mb-4">
                    {[
                        { label:'Submitted this month', val: fmtCurrency(monthly.total_submitted), color:'#4f46e5' },
                        { label:'Approved',             val: fmtCurrency(monthly.total_approved),  color:'#059669' },
                        { label:'Pending approval',     val: monthly.pending_count,  color:'#d97706', unit:'claims' },
                    ].map((k, i) => (
                        <Col key={i} xs={6} md={4}>
                            <Card className="border-0 shadow-sm p-3 text-center"
                                style={{ borderBottom:`3px solid ${k.color}` }}>
                                <div style={{ fontSize:22, fontWeight:700, color:k.color }}>
                                    {k.val}{k.unit && ` ${k.unit}`}
                                </div>
                                <div className="text-muted small">{k.label}</div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            <div className="d-flex justify-content-end mb-3">
                <Button size="sm" variant="primary" onClick={() => setShowNew(true)}>
                    <Plus size={14} className="me-1"/>Submit New Claim
                </Button>
            </div>

            {claims.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center">
                    <Receipt size={40} className="mx-auto mb-3 text-muted opacity-50"/>
                    <p className="text-muted">No expense claims yet.</p>
                    <Button variant="primary" size="sm" onClick={() => setShowNew(true)}>
                        <Plus size={14} className="me-1"/>Submit your first claim
                    </Button>
                </Card>
            ) : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize:11 }}>
                                <th className="ps-4">#</th><th>Title</th><th>Category</th>
                                <th>Date</th><th>Amount</th><th>Approved</th>
                                <th>Status</th><th className="pe-4 text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {claims.map(c => (
                                <tr key={c.id}>
                                    <td className="ps-4 text-muted small">{c.claim_number}</td>
                                    <td>
                                        <div className="fw-medium">{c.title}</div>
                                        {c.review_remarks && (
                                            <div className="text-muted" style={{ fontSize:10 }}>
                                                Note: {c.review_remarks}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {c.category_name
                                            ? <Badge bg="light" text="dark">{c.category_name}</Badge>
                                            : <span className="text-muted">—</span>}
                                    </td>
                                    <td>{fmtDate(c.expense_date)}</td>
                                    <td className="fw-bold">{fmtCurrency(c.amount)}</td>
                                    <td className={c.approved_amount ? 'text-success fw-bold' : 'text-muted'}>
                                        {c.approved_amount ? fmtCurrency(c.approved_amount) : '—'}
                                        {c.approved_amount && parseFloat(c.approved_amount) !== parseFloat(c.amount) && (
                                            <div style={{ fontSize:10, color:'#d97706' }}>Adjusted</div>
                                        )}
                                    </td>
                                    <td>
                                        <Badge bg={STATUS_CONFIG[c.status]?.bg || 'secondary'}>
                                            {STATUS_CONFIG[c.status]?.label || c.status}
                                        </Badge>
                                    </td>
                                    <td className="pe-4 text-end">
                                        <div className="d-flex gap-1 justify-content-end">
                                            <Button size="sm" variant="outline-secondary" className="py-0"
                                                onClick={() => setViewing(c)}>
                                                <Eye size={12}/>
                                            </Button>
                                            {c.status === 'pending' && (
                                                <Button size="sm" variant="outline-danger" className="py-0"
                                                    onClick={() => withdraw(c.id)}>
                                                    <X size={12}/>
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}

            {showNew && (
                <NewClaimModal onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }}/>
            )}
            {viewing && (
                <ClaimDetailModal claim={viewing} onClose={() => setViewing(null)} readOnly/>
            )}
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// PENDING TAB — approval queue
// ════════════════════════════════════════════════════════════════════════════
const PendingTab = ({ isHR }) => {
    const [claims,  setClaims]  = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewing, setViewing] = useState(null);

    const load = () => {
        setLoading(true);
        axiosInstance.get('/expenses/pending')
            .then(r => setClaims(r.data.data || []))
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const approve = async (id, approvedAmount, remarks) => {
        try {
            await axiosInstance.patch(`/expenses/${id}/approve`, { approved_amount: approvedAmount, remarks });
            toast.success('Claim approved ✓');
            setViewing(null);
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    const reject = async (id, remarks) => {
        if (!remarks?.trim()) { toast.error('Rejection reason required'); return; }
        try {
            await axiosInstance.patch(`/expenses/${id}/reject`, { remarks });
            toast.success('Claim rejected');
            setViewing(null);
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="warning"/></div>;

    if (claims.length === 0) return (
        <Card className="border-0 shadow-sm p-5 text-center">
            <CheckCircle size={40} className="mx-auto mb-3 text-success opacity-75"/>
            <p className="text-muted">No pending expense claims. You're all caught up!</p>
        </Card>
    );

    return (
        <>
            <Alert variant="warning" className="small py-2 mb-3">
                <AlertCircle size={14} className="me-1"/>
                {claims.length} claim{claims.length !== 1 ? 's' : ''} awaiting your approval
            </Alert>

            <Card className="border-0 shadow-sm">
                <Table hover responsive className="mb-0 small align-middle">
                    <thead className="bg-light">
                        <tr className="text-muted text-uppercase" style={{ fontSize:11 }}>
                            <th className="ps-4">Employee</th><th>#</th>
                            <th>Title</th><th>Category</th>
                            <th>Date</th><th>Amount</th>
                            <th>Receipt</th><th className="pe-4 text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {claims.map(c => (
                            <tr key={c.id}>
                                <td className="ps-4">
                                    <div className="fw-medium">{c.employee_name}</div>
                                    <div className="text-muted" style={{ fontSize:10 }}>
                                        {c.employee_code} · {c.department}
                                    </div>
                                </td>
                                <td className="text-muted small">{c.claim_number}</td>
                                <td>
                                    <div className="fw-medium">{c.title}</div>
                                    {c.description && (
                                        <div className="text-muted" style={{ fontSize:11,
                                            overflow:'hidden', maxWidth:160,
                                            whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
                                            {c.description}
                                        </div>
                                    )}
                                </td>
                                <td>
                                    {c.category_name
                                        ? <Badge bg="light" text="dark">{c.category_name}</Badge>
                                        : <span className="text-muted">—</span>}
                                    {c.category_max && parseFloat(c.amount) > parseFloat(c.category_max) && (
                                        <div className="text-danger" style={{ fontSize:10 }}>⚠ Over limit</div>
                                    )}
                                </td>
                                <td>{fmtDate(c.expense_date)}</td>
                                <td><span className="fw-bold">{fmtCurrency(c.amount)}</span></td>
                                <td>
                                    {c.receipt_url
                                        ? <a href={c.receipt_url} target="_blank" rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-secondary py-0">
                                            <Eye size={12}/> View
                                          </a>
                                        : <span className="text-muted small">None</span>}
                                </td>
                                <td className="pe-4 text-end">
                                    <Button size="sm" variant="outline-primary" className="py-0"
                                        onClick={() => setViewing(c)}>
                                        Review <ChevronRight size={12}/>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Card>

            {viewing && (
                <ApprovalModal
                    claim={viewing}
                    onClose={() => setViewing(null)}
                    onApprove={approve}
                    onReject={reject}
                />
            )}
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// ALL CLAIMS TAB (HR)
// ════════════════════════════════════════════════════════════════════════════
const AllClaimsTab = () => {
    const [claims,  setClaims]  = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status:'', month: CURR_MONTH, year: CURR_YEAR });
    const [selected, setSelected] = useState([]);
    const [paying, setPaying]   = useState(false);
    const [paidVia, setPaidVia] = useState('Bank Transfer');

    const load = () => {
        setLoading(true);
        axiosInstance.get('/expenses', { params: filters })
            .then(r => setClaims(r.data.data?.claims || []))
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [filters]);

    const downloadCSV = async (type) => {
        try {
            const params = type === 'payroll'
                ? { month: filters.month, year: filters.year }
                : { year: filters.year, status: filters.status || undefined };
            const res = await axiosInstance.get(`/expenses/export/${type}`, {
                params, responseType: 'blob',
            });
            const url = URL.createObjectURL(res.data);
            const a   = document.createElement('a');
            a.href = url;
            a.download = type === 'payroll'
                ? `expense_payroll_${filters.year}_${String(filters.month).padStart(2,'0')}.csv`
                : `expense_claims_${filters.year}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Export ready');
        } catch { toast.error('Export failed'); }
    };

    const markPaid = async () => {
        if (!selected.length) return;
        setPaying(true);
        try {
            const res = await axiosInstance.patch('/expenses/bulk-mark-paid', {
                claim_ids: selected, paid_via: paidVia,
            });
            toast.success(`${res.data.data?.paid_count} claims marked as paid`);
            setSelected([]);
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setPaying(false); }
    };

    const approved = claims.filter(c => c.status === 'approved');
    const toggle   = (id) => setSelected(p => p.includes(id) ? p.filter(x=>x!==id) : [...p, id]);
    const allChecked = approved.length > 0 && approved.every(c => selected.includes(c.id));

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;

    return (
        <>
            <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
                <Form.Select size="sm" style={{ width:130 }} value={filters.status}
                    onChange={e => setFilters(p => ({...p, status: e.target.value}))}>
                    <option value="">All status</option>
                    {Object.entries(STATUS_CONFIG).map(([k,v]) =>
                        <option key={k} value={k}>{v.label}</option>)}
                </Form.Select>
                <Form.Select size="sm" style={{ width:110 }} value={filters.month}
                    onChange={e => setFilters(p => ({...p, month: e.target.value}))}>
                    {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
                </Form.Select>
                <Form.Select size="sm" style={{ width:90 }} value={filters.year}
                    onChange={e => setFilters(p => ({...p, year: e.target.value}))}>
                    {[CURR_YEAR, CURR_YEAR-1, CURR_YEAR-2].map(y =>
                        <option key={y}>{y}</option>)}
                </Form.Select>

                <div className="ms-auto d-flex gap-2 align-items-center">
                    {selected.length > 0 && (
                        <>
                            <Form.Select size="sm" style={{ width:160 }}
                                value={paidVia} onChange={e => setPaidVia(e.target.value)}>
                                {['Bank Transfer','Cheque','Cash','UPI','Payroll'].map(v =>
                                    <option key={v}>{v}</option>)}
                            </Form.Select>
                            <Button size="sm" variant="success" onClick={markPaid} disabled={paying}>
                                {paying ? <Spinner size="sm"/> : <CheckCircle size={14} className="me-1"/>}
                                Mark {selected.length} Paid
                            </Button>
                        </>
                    )}
                    <Button size="sm" variant="outline-secondary"
                        onClick={() => downloadCSV('payroll')}>
                        <Download size={13} className="me-1"/>Payroll CSV
                    </Button>
                    <Button size="sm" variant="outline-secondary"
                        onClick={() => downloadCSV('claims')}>
                        <Download size={13} className="me-1"/>All CSV
                    </Button>
                </div>
            </div>

            {selected.length > 0 && (
                <Alert variant="success" className="small py-2 mb-3">
                    {selected.length} claim(s) selected · Total: {fmtCurrency(
                        approved.filter(c => selected.includes(c.id))
                            .reduce((s,c) => s + parseFloat(c.approved_amount||0), 0)
                    )}
                </Alert>
            )}

            <Card className="border-0 shadow-sm">
                <Table hover responsive className="mb-0 small align-middle">
                    <thead className="bg-light">
                        <tr className="text-muted text-uppercase" style={{ fontSize:11 }}>
                            <th className="ps-4">
                                <Form.Check checked={allChecked}
                                    onChange={e => setSelected(e.target.checked ? approved.map(c=>c.id) : [])}/>
                            </th>
                            <th>Employee</th><th>#</th><th>Title</th>
                            <th>Date</th><th>Claimed</th><th>Approved</th>
                            <th>Status</th><th className="pe-4">Receipt</th>
                        </tr>
                    </thead>
                    <tbody>
                        {claims.map(c => (
                            <tr key={c.id}
                                style={{ background: selected.includes(c.id) ? '#f0fdf4' : undefined }}>
                                <td className="ps-4">
                                    {c.status === 'approved' && (
                                        <Form.Check checked={selected.includes(c.id)}
                                            onChange={() => toggle(c.id)}/>
                                    )}
                                </td>
                                <td>
                                    <div className="fw-medium">{c.employee_name}</div>
                                    <div className="text-muted" style={{ fontSize:10 }}>
                                        {c.employee_code} · {c.department}
                                    </div>
                                </td>
                                <td className="text-muted">{c.claim_number}</td>
                                <td>
                                    <div className="fw-medium">{c.title}</div>
                                    <div style={{ fontSize:10 }} className="text-muted">{c.category_name}</div>
                                </td>
                                <td>{fmtDate(c.expense_date)}</td>
                                <td className="fw-bold">{fmtCurrency(c.amount)}</td>
                                <td className="text-success fw-bold">
                                    {c.approved_amount ? fmtCurrency(c.approved_amount) : '—'}
                                </td>
                                <td>
                                    <Badge bg={STATUS_CONFIG[c.status]?.bg||'secondary'}>
                                        {STATUS_CONFIG[c.status]?.label||c.status}
                                    </Badge>
                                    {c.paid_at && (
                                        <div className="text-muted" style={{ fontSize:10 }}>
                                            {fmtDate(c.paid_at)} · {c.paid_via}
                                        </div>
                                    )}
                                </td>
                                <td className="pe-4">
                                    {c.receipt_url
                                        ? <a href={c.receipt_url} target="_blank" rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-secondary py-0">
                                            <Eye size={12}/>
                                          </a>
                                        : <span className="text-muted">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Card>
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS TAB
// ════════════════════════════════════════════════════════════════════════════
const StatsTab = () => {
    const [data, setData]     = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axiosInstance.get('/expenses/stats')
            .then(r => setData(r.data.data))
            .catch(() => toast.error('Failed'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-center py-5"><Spinner animation="border"/></div>;
    if (!data)   return null;

    const s = data.summary || {};
    const kpis = [
        { label:'Total Submitted', val:s.total_claims||0,                      color:'#4f46e5' },
        { label:'Pending Approval', val:s.pending||0,                          color:'#d97706' },
        { label:'Approved (unpaid)', val:fmtCurrency(s.pending_payout),        color:'#059669' },
        { label:'Total Paid Out',   val:fmtCurrency(s.total_paid),             color:'#0891b2' },
        { label:'Avg Review Time',  val:s.avg_review_hours ? `${s.avg_review_hours}h` : '—', color:'#7c3aed' },
        { label:'Total Approved',   val:fmtCurrency(s.total_approved_amount),  color:'#dc2626' },
    ];

    return (
        <>
            <Row className="g-3 mb-4">
                {kpis.map((k, i) => (
                    <Col key={i} xs={6} md={4} lg={2}>
                        <Card className="border-0 shadow-sm p-3 text-center"
                            style={{ borderBottom:`3px solid ${k.color}` }}>
                            <div style={{ fontSize:20, fontWeight:700, color:k.color }}>{k.val}</div>
                            <div className="text-muted" style={{ fontSize:11 }}>{k.label}</div>
                        </Card>
                    </Col>
                ))}
            </Row>
            <Row className="g-4">
                <Col md={6}>
                    <Card className="border-0 shadow-sm p-4">
                        <h6 className="fw-bold mb-3">By Category (approved amount)</h6>
                        {(data.by_category || []).map((c, i) => (
                            <div key={i} className="mb-3">
                                <div className="d-flex justify-content-between small mb-1">
                                    <span className="fw-medium">{c.name}</span>
                                    <span>{fmtCurrency(c.total_amount)}</span>
                                </div>
                                <ProgressBar
                                    now={data.by_category[0]?.total_amount
                                        ? (c.total_amount/data.by_category[0].total_amount)*100 : 0}
                                    style={{ height:8 }}
                                    variant={['primary','success','info','warning','danger'][i%5]}/>
                                <div className="text-muted" style={{ fontSize:10 }}>{c.claims} claims</div>
                            </div>
                        ))}
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="border-0 shadow-sm p-4">
                        <h6 className="fw-bold mb-3">Monthly Trend (last 6 months)</h6>
                        {(data.monthly_trend || []).map((m, i) => (
                            <div key={i} className="d-flex justify-content-between align-items-center mb-2 small">
                                <span className="text-muted">{MONTHS[m.month-1]} {m.year}</span>
                                <div className="text-end">
                                    <div className="fw-bold">{fmtCurrency(m.total_approved)}</div>
                                    <div className="text-muted" style={{ fontSize:10 }}>
                                        {m.claims} claims · submitted {fmtCurrency(m.total_submitted)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </Card>
                </Col>
            </Row>
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// SETTINGS TAB
// ════════════════════════════════════════════════════════════════════════════
const SettingsTab = () => {
    const [policy, setPolicy]   = useState(null);
    const [categories, setCats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving,  setSaving]  = useState(false);
    const [showCatForm, setShowCatForm] = useState(false);
    const [editCat, setEditCat] = useState(null);

    const load = () => {
        setLoading(true);
        Promise.all([
            axiosInstance.get('/expenses/policy'),
            axiosInstance.get('/expenses/categories'),
        ]).then(([p, c]) => {
            setPolicy(p.data.data || {});
            setCats(c.data.data || []);
        }).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const savePolicy = async () => {
        setSaving(true);
        try {
            await axiosInstance.put('/expenses/policy', policy);
            toast.success('Policy saved');
        } catch { toast.error('Failed'); }
        finally { setSaving(false); }
    };

    const deactivateCat = async (id) => {
        try {
            await axiosInstance.delete(`/expenses/categories/${id}`);
            toast.success('Category removed');
            load();
        } catch { toast.error('Failed'); }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border"/></div>;

    return (
        <Row className="g-4">
            {/* Policy */}
            <Col lg={6}>
                <Card className="border-0 shadow-sm p-4">
                    <h6 className="fw-bold mb-3">Expense Policy</h6>
                    <Row className="g-3">
                        {[
                            { field:'monthly_limit',        label:'Monthly limit per employee (₹)',  hint:'0 = no limit'   },
                            { field:'single_claim_limit',   label:'Max per single claim (₹)',         hint:'0 = no limit'   },
                            { field:'auto_approve_below',   label:'Auto-approve below (₹)',           hint:'0 = disabled'   },
                            { field:'require_receipt_above',label:'Receipt required above (₹)',       hint:'0 = always'     },
                        ].map(f => (
                            <Col key={f.field} xs={12}>
                                <Form.Label className="small fw-bold">{f.label}</Form.Label>
                                <InputGroup size="sm">
                                    <InputGroup.Text>₹</InputGroup.Text>
                                    <Form.Control type="number" min={0}
                                        value={policy?.[f.field] || ''}
                                        onChange={e => setPolicy(p => ({...p, [f.field]: e.target.value}))}/>
                                </InputGroup>
                                <Form.Text className="text-muted">{f.hint}</Form.Text>
                            </Col>
                        ))}
                        <Col xs={12}>
                            <Button variant="primary" size="sm" onClick={savePolicy} disabled={saving}>
                                {saving ? <Spinner size="sm"/> : 'Save Policy'}
                            </Button>
                        </Col>
                    </Row>
                </Card>
            </Col>

            {/* Categories */}
            <Col lg={6}>
                <Card className="border-0 shadow-sm p-4">
                    <div className="d-flex justify-content-between mb-3">
                        <h6 className="fw-bold mb-0">Expense Categories</h6>
                        <Button size="sm" variant="outline-primary"
                            onClick={() => { setEditCat(null); setShowCatForm(true); }}>
                            <Plus size={13} className="me-1"/>Add
                        </Button>
                    </div>
                    {categories.map(c => (
                        <div key={c.id}
                            className="d-flex justify-content-between align-items-center py-2"
                            style={{ borderBottom:'0.5px solid #f1f5f9' }}>
                            <div>
                                <div className="small fw-medium">{c.name}</div>
                                <div className="text-muted" style={{ fontSize:10 }}>
                                    {c.max_amount ? `Max: ${fmtCurrency(c.max_amount)}` : 'No limit'}
                                    {c.requires_receipt ? ' · Receipt required' : ''}
                                </div>
                            </div>
                            <div className="d-flex gap-1">
                                <Button size="sm" variant="outline-secondary" className="py-0"
                                    onClick={() => { setEditCat(c); setShowCatForm(true); }}>
                                    Edit
                                </Button>
                                <Button size="sm" variant="outline-danger" className="py-0"
                                    onClick={() => deactivateCat(c.id)}>
                                    <X size={11}/>
                                </Button>
                            </div>
                        </div>
                    ))}
                </Card>
            </Col>

            {showCatForm && (
                <CategoryFormModal
                    category={editCat}
                    onClose={() => setShowCatForm(false)}
                    onDone={() => { setShowCatForm(false); load(); }}
                />
            )}
        </Row>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// MODALS
// ════════════════════════════════════════════════════════════════════════════

// ── New Claim Modal ───────────────────────────────────────────────────────────
const NewClaimModal = ({ onClose, onDone }) => {
    const [categories, setCats] = useState([]);
    const [policy,     setPolicy] = useState(null);
    const [form, setForm]   = useState({
        title:'', description:'', category_id:'',
        expense_date: new Date().toISOString().slice(0,10),
        amount:'', currency:'INR',
    });
    const [receipt, setReceipt]   = useState(null);
    const [preview, setPreview]   = useState(null);
    const [saving,  setSaving]    = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        Promise.all([
            axiosInstance.get('/expenses/categories'),
            axiosInstance.get('/expenses/policy'),
        ]).then(([c, p]) => {
            setCats(c.data.data || []);
            setPolicy(p.data.data);
        }).catch(()=>{});
    }, []);

    const handleFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5*1024*1024) { toast.error('File too large (max 5 MB)'); return; }
        setReceipt(file);
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = e => setPreview(e.target.result);
            reader.readAsDataURL(file);
        } else {
            setPreview('pdf');
        }
    };

    const submit = async () => {
        if (!form.title.trim())  return toast.error('Title is required');
        if (!form.amount || parseFloat(form.amount) <= 0)
                                 return toast.error('Amount must be greater than 0');
        if (!form.expense_date)  return toast.error('Expense date is required');

        setSaving(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k,v]) => v && fd.append(k,v));
            if (receipt) fd.append('receipt', receipt);
            const res = await axiosInstance.post('/expenses', fd);
            const d   = res.data.data;
            toast.success(d?.auto_approved
                ? `Claim ${d.claim_number} auto-approved! 🎉`
                : `Claim ${d?.claim_number} submitted for approval`);
            onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit'); }
        finally { setSaving(false); }
    };

    const amt     = parseFloat(form.amount) || 0;
    const needRec = policy?.require_receipt_above > 0 && amt >= parseFloat(policy.require_receipt_above||100);
    const autoApp = policy?.auto_approve_below > 0 && amt > 0 && amt < parseFloat(policy.auto_approve_below);

    return (
        <Modal show onHide={onClose} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <Receipt size={18} className="me-2 text-primary"/>Submit Expense Claim
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-0">
                    {/* Form */}
                    <Col md={7} className="pe-md-4">
                        <Row className="g-3">
                            <Col xs={12}>
                                <Form.Label className="small fw-bold">Title *</Form.Label>
                                <Form.Control placeholder="e.g. Client dinner at Spice Garden"
                                    value={form.title}
                                    onChange={e => setForm(p => ({...p, title: e.target.value}))}/>
                            </Col>
                            <Col xs={6}>
                                <Form.Label className="small fw-bold">Category</Form.Label>
                                <Form.Select value={form.category_id}
                                    onChange={e => setForm(p => ({...p, category_id: e.target.value}))}>
                                    <option value="">— Select —</option>
                                    {categories.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}{c.max_amount ? ` (max ₹${parseFloat(c.max_amount).toLocaleString('en-IN')})` : ''}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col xs={6}>
                                <Form.Label className="small fw-bold">Expense Date *</Form.Label>
                                <Form.Control type="date" value={form.expense_date}
                                    onChange={e => setForm(p => ({...p, expense_date: e.target.value}))}/>
                            </Col>
                            <Col xs={8}>
                                <Form.Label className="small fw-bold">Amount *</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>₹</InputGroup.Text>
                                    <Form.Control type="number" placeholder="0.00" min="0" step="0.01"
                                        value={form.amount}
                                        onChange={e => setForm(p => ({...p, amount: e.target.value}))}/>
                                </InputGroup>
                                {autoApp && (
                                    <div className="text-success small mt-1">
                                        ✓ This will be auto-approved!
                                    </div>
                                )}
                            </Col>
                            <Col xs={4}>
                                <Form.Label className="small fw-bold">Currency</Form.Label>
                                <Form.Select value={form.currency}
                                    onChange={e => setForm(p => ({...p, currency: e.target.value}))}>
                                    {['INR','USD','EUR','GBP'].map(c => <option key={c}>{c}</option>)}
                                </Form.Select>
                            </Col>
                            <Col xs={12}>
                                <Form.Label className="small fw-bold">Description</Form.Label>
                                <Form.Control as="textarea" rows={2}
                                    placeholder="Purpose, attendees, or any other context…"
                                    value={form.description}
                                    onChange={e => setForm(p => ({...p, description: e.target.value}))}/>
                            </Col>
                        </Row>
                    </Col>

                    {/* Receipt upload */}
                    <Col md={5} className="ps-md-4 border-start-md mt-4 mt-md-0">
                        <Form.Label className="small fw-bold">
                            Receipt / Invoice
                            {needRec && <span className="text-danger ms-1">*required</span>}
                        </Form.Label>

                        {!preview ? (
                            <div
                                onClick={() => fileRef.current?.click()}
                                style={{
                                    border:'2px dashed #d1d5db', borderRadius:12,
                                    padding:'32px 16px', textAlign:'center',
                                    cursor:'pointer', background:'#fafafa',
                                    transition:'border-color .2s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.borderColor='#4f46e5'}
                                onMouseLeave={e => e.currentTarget.style.borderColor='#d1d5db'}>
                                <Upload size={28} className="text-muted mb-2"/>
                                <div className="small fw-medium text-muted">
                                    Click to upload receipt
                                </div>
                                <div className="text-muted" style={{ fontSize:11 }}>
                                    JPG, PNG, PDF · max 5 MB
                                </div>
                            </div>
                        ) : (
                            <div style={{ position:'relative' }}>
                                {preview === 'pdf' ? (
                                    <div className="p-3 rounded text-center"
                                        style={{ background:'#f1f5f9', border:'1px solid #e2e8f0' }}>
                                        <FileText size={32} className="text-muted mb-1"/>
                                        <div className="small text-muted">{receipt?.name}</div>
                                    </div>
                                ) : (
                                    <img src={preview} alt="Receipt preview"
                                        className="rounded w-100"
                                        style={{ maxHeight:200, objectFit:'contain',
                                            border:'1px solid #e2e8f0' }}/>
                                )}
                                <Button size="sm" variant="danger"
                                    style={{ position:'absolute', top:8, right:8 }}
                                    onClick={() => { setReceipt(null); setPreview(null); }}>
                                    <X size={12}/>
                                </Button>
                            </div>
                        )}
                        <input ref={fileRef} type="file" className="d-none"
                            accept="image/*,application/pdf" onChange={handleFile}/>

                        {policy && (
                            <div className="mt-3 p-3 rounded" style={{ background:'#f8fafc', fontSize:12 }}>
                                <div className="fw-bold mb-1">Policy limits</div>
                                {policy.monthly_limit > 0 && (
                                    <div className="text-muted">Monthly: {fmtCurrency(policy.monthly_limit)}</div>
                                )}
                                {policy.single_claim_limit > 0 && (
                                    <div className="text-muted">Per claim: {fmtCurrency(policy.single_claim_limit)}</div>
                                )}
                                {policy.auto_approve_below > 0 && (
                                    <div className="text-success">
                                        Auto-approve below {fmtCurrency(policy.auto_approve_below)}
                                    </div>
                                )}
                            </div>
                        )}
                    </Col>
                </Row>

                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" className="flex-grow-1 fw-bold"
                        onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : '📤 Submit Claim'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// ── Approval Modal ────────────────────────────────────────────────────────────
const ApprovalModal = ({ claim, onClose, onApprove, onReject }) => {
    const [approvedAmt, setApprovedAmt] = useState(claim.amount);
    const [remarks,     setRemarks]     = useState('');
    const [mode,        setMode]        = useState('approve'); // 'approve' | 'reject'

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    Review Expense Claim
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Claim summary */}
                <div className="p-3 rounded mb-4" style={{ background:'#f8fafc' }}>
                    <div className="fw-bold">{claim.title}</div>
                    <div className="text-muted small">
                        {claim.employee_name} · {claim.department} · {fmtDate(claim.expense_date)}
                    </div>
                    <div className="mt-2 d-flex gap-3">
                        <div>
                            <div className="text-muted" style={{ fontSize:11 }}>Claimed</div>
                            <div className="fw-bold fs-5">{fmtCurrency(claim.amount)}</div>
                        </div>
                        {claim.category_name && (
                            <div>
                                <div className="text-muted" style={{ fontSize:11 }}>Category</div>
                                <Badge bg="light" text="dark">{claim.category_name}</Badge>
                            </div>
                        )}
                    </div>
                    {claim.description && (
                        <div className="text-muted small mt-2">{claim.description}</div>
                    )}
                    {claim.receipt_url && (
                        <a href={claim.receipt_url} target="_blank" rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-secondary mt-2 py-0">
                            <Eye size={12} className="me-1"/>View Receipt
                        </a>
                    )}
                </div>

                {/* Mode toggle */}
                <div className="d-flex gap-2 mb-3">
                    <Button size="sm"
                        variant={mode==='approve' ? 'success' : 'outline-secondary'}
                        onClick={() => setMode('approve')}>
                        ✓ Approve
                    </Button>
                    <Button size="sm"
                        variant={mode==='reject' ? 'danger' : 'outline-secondary'}
                        onClick={() => setMode('reject')}>
                        ✗ Reject
                    </Button>
                </div>

                {mode === 'approve' ? (
                    <>
                        <Form.Label className="small fw-bold">Approved Amount</Form.Label>
                        <InputGroup className="mb-3">
                            <InputGroup.Text>₹</InputGroup.Text>
                            <Form.Control type="number" value={approvedAmt} min={0}
                                onChange={e => setApprovedAmt(e.target.value)}/>
                        </InputGroup>
                        {parseFloat(approvedAmt) !== parseFloat(claim.amount) && (
                            <Alert variant="warning" className="small py-1">
                                You're approving a different amount than claimed. The employee will be notified.
                            </Alert>
                        )}
                        <Form.Control placeholder="Remarks (optional)"
                            value={remarks} onChange={e => setRemarks(e.target.value)}/>
                        <div className="d-flex gap-2 mt-4">
                            <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                            <Button variant="success" className="flex-grow-1 fw-bold"
                                onClick={() => onApprove(claim.id, approvedAmt, remarks)}>
                                ✓ Approve {fmtCurrency(approvedAmt)}
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <Form.Label className="small fw-bold">Reason for rejection *</Form.Label>
                        <Form.Control as="textarea" rows={3}
                            placeholder="Explain why this claim is being rejected…"
                            value={remarks} onChange={e => setRemarks(e.target.value)}/>
                        <div className="d-flex gap-2 mt-4">
                            <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                            <Button variant="danger" className="flex-grow-1 fw-bold"
                                onClick={() => onReject(claim.id, remarks)}
                                disabled={!remarks.trim()}>
                                ✗ Reject Claim
                            </Button>
                        </div>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

// ── Category Form Modal ───────────────────────────────────────────────────────
const CategoryFormModal = ({ category, onClose, onDone }) => {
    const [form, setForm] = useState({
        name:    category?.name    || '',
        description: category?.description || '',
        max_amount:  category?.max_amount  || '',
        requires_receipt: category?.requires_receipt !== 0,
    });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!form.name.trim()) return toast.error('Category name required');
        setSaving(true);
        try {
            if (category) {
                await axiosInstance.put(`/expenses/categories/${category.id}`, form);
                toast.success('Category updated');
            } else {
                await axiosInstance.post('/expenses/categories', form);
                toast.success('Category created');
            }
            onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    return (
        <Modal show onHide={onClose} centered size="sm">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    {category ? 'Edit Category' : 'New Category'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Name *</Form.Label>
                        <Form.Control placeholder="e.g. Travel, Meals, Equipment"
                            value={form.name}
                            onChange={e => setForm(p => ({...p, name: e.target.value}))}/>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Max Amount (₹)</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>₹</InputGroup.Text>
                            <Form.Control type="number" placeholder="Leave blank for no limit"
                                value={form.max_amount}
                                onChange={e => setForm(p => ({...p, max_amount: e.target.value}))}/>
                        </InputGroup>
                    </Col>
                    <Col xs={12}>
                        <Form.Check type="switch" label="Always require receipt for this category"
                            checked={form.requires_receipt}
                            onChange={e => setForm(p => ({...p, requires_receipt: e.target.checked}))}/>
                    </Col>
                </Row>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" className="flex-grow-1" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : (category ? 'Save' : 'Create')}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// ── Claim detail (read-only) ──────────────────────────────────────────────────
const ClaimDetailModal = ({ claim, onClose }) => (
    <Modal show onHide={onClose} centered>
        <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold fs-6">{claim.claim_number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <div className="mb-3">
                <Badge bg={STATUS_CONFIG[claim.status]?.bg||'secondary'}>
                    {STATUS_CONFIG[claim.status]?.label}
                </Badge>
            </div>
            {[
                { label:'Title',     val: claim.title },
                { label:'Category',  val: claim.category_name || '—' },
                { label:'Date',      val: fmtDate(claim.expense_date) },
                { label:'Amount',    val: fmtCurrency(claim.amount) },
                { label:'Approved',  val: claim.approved_amount ? fmtCurrency(claim.approved_amount) : '—' },
                { label:'Reviewed by', val: claim.reviewed_by_name || '—' },
                { label:'Reviewed at', val: fmtDate(claim.reviewed_at) },
                { label:'Remarks',   val: claim.review_remarks || '—' },
                { label:'Paid at',   val: claim.paid_at ? fmtDate(claim.paid_at) : '—' },
            ].map((r,i) => (
                <div key={i} className="d-flex justify-content-between py-2"
                    style={{ borderBottom:'0.5px solid #f1f5f9', fontSize:13 }}>
                    <span className="text-muted">{r.label}</span>
                    <span className="fw-medium">{r.val}</span>
                </div>
            ))}
            {claim.receipt_url && (
                <div className="mt-3">
                    <a href={claim.receipt_url} target="_blank" rel="noopener noreferrer"
                        className="btn btn-outline-secondary btn-sm w-100">
                        <Eye size={14} className="me-1"/>View Receipt
                    </a>
                </div>
            )}
        </Modal.Body>
    </Modal>
);

export default ExpensesPage;
